import type { ScimGroupMapping, UpdateScimGroupMappingRequest } from "@/types";

/**
 * One table row: a role the group grants, and where it applies.
 * A workspace entry with `roleId: null` grants membership without a role.
 */
export type MappingEntry =
  | { level: "organization"; roleId: string }
  | { level: "workspace"; roleId: string | null; workspaceIds: string[] };

// Pivots the API shape (workspace → roles) into one entry per role.
export const toMappingEntries = (group: ScimGroupMapping): MappingEntry[] => {
  const organizationEntries: MappingEntry[] = group.role_ids.map((roleId) => ({
    level: "organization",
    roleId,
  }));

  const workspaceIdsByRole = new Map<string | null, string[]>();
  for (const workspace of group.workspaces) {
    const roleIds = workspace.role_ids ?? [];
    const keys = roleIds.length > 0 ? roleIds : [null];
    for (const roleId of keys) {
      const workspaceIds = workspaceIdsByRole.get(roleId) ?? [];
      workspaceIdsByRole.set(roleId, [...workspaceIds, workspace.workspace_id]);
    }
  }

  const workspaceEntries: MappingEntry[] = [...workspaceIdsByRole]
    // "No role" goes last
    .sort(([a], [b]) => Number(a === null) - Number(b === null))
    .map(([roleId, workspaceIds]) => ({
      level: "workspace",
      roleId,
      workspaceIds,
    }));

  return [...organizationEntries, ...workspaceEntries];
};

// Builds the full PUT body back from the entries: anything left out is revoked.
export const toMappingRequest = (
  entries: MappingEntry[]
): UpdateScimGroupMappingRequest => {
  const roleIds = new Set<string>();
  const roleIdsByWorkspace = new Map<string, Set<string>>();

  for (const entry of entries) {
    if (entry.level === "organization") {
      roleIds.add(entry.roleId);
      continue;
    }
    for (const workspaceId of entry.workspaceIds) {
      const workspaceRoleIds =
        roleIdsByWorkspace.get(workspaceId) ?? new Set<string>();
      if (entry.roleId) {
        workspaceRoleIds.add(entry.roleId);
      }
      roleIdsByWorkspace.set(workspaceId, workspaceRoleIds);
    }
  }

  return {
    role_ids: [...roleIds],
    workspaces: [...roleIdsByWorkspace].map(
      ([workspace_id, workspaceRoleIds]) => ({
        workspace_id,
        role_ids: [...workspaceRoleIds],
      })
    ),
  };
};

export const isConfigured = (group: ScimGroupMapping) =>
  group.role_ids.length > 0 || group.workspaces.length > 0;

export const getGroupName = (group: ScimGroupMapping) =>
  group.directory_group_name ?? group.directory_group_id;

// Detail text of a 422; the admin axios instance rejects with a plain object, not an Error
export const getErrorDetail = (error: unknown) =>
  typeof error === "object" &&
  error !== null &&
  "statusCode" in error &&
  error.statusCode === 422 &&
  "message" in error &&
  typeof error.message === "string"
    ? error.message
    : "";
